'use client';

import cn from 'classnames';
import { FormContext_State, useFormContext } from '../FormContext';
import { useEffect, useState } from 'react';

export interface ProgressBarProps {
	testId?: string;
}

const ProgressBar = <
	Steps extends string,
	State extends FormContext_State<Steps>,
	Action,
>(
	props: ProgressBarProps,
) => {
	const { testId = 'ProgressBar' } = props;
	const { state } = useFormContext<Steps, State, Action>();
	const [isLoaded, setIsLoaded] = useState(false);

	useEffect(() => {
		// Trigger the animation after a small delay
		const timer = setTimeout(() => setIsLoaded(true), 50);
		return () => clearTimeout(timer);
	}, []);

	const { maxSteps: totalSteps, currentStep } = state;

	if (totalSteps > 12)
		console.warn('ProgressBar supports a maximum of 12 steps');

	return (
		<div data-testid={testId}>
			<div
				className={cn('grid gap-1', {
					'grid-cols-1': totalSteps === 1,
					'grid-cols-2': totalSteps === 2,
					'grid-cols-3': totalSteps === 3,
					'grid-cols-4': totalSteps === 4,
					'grid-cols-5': totalSteps === 5,
					'grid-cols-6': totalSteps === 6,
					'grid-cols-7': totalSteps === 7,
					'grid-cols-8': totalSteps === 8,
					'grid-cols-9': totalSteps === 9,
					'grid-cols-10': totalSteps === 10,
					'grid-cols-11': totalSteps === 11,
					'grid-cols-12': totalSteps === 12,
				})}
			>
				{Array.from({ length: totalSteps }, (_, i) => i).map((i) => (
					<div
						key={i}
						className={cn(
							'h-2 transition-all duration-500 ease-linear',
							i < currentStep ? 'bg-sky-600' : 'bg-slate-100',
						)}
					>
						{/* additional div so the blue animates over the gray */}
						<div
							className={cn(
								'h-full bg-sky-600 transition-all duration-500 ease-linear',
								{
									'w-full': isLoaded && i === currentStep,
									'w-0': !isLoaded || i > currentStep,
								},
							)}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export default ProgressBar;
